import axios from 'axios';
import Qs from 'qs';
import jwtDecode from 'jwt-decode';
import store from '@/config/store';
import * as Basil from '@/plugins/basil.min';

const meta = {
    version: '1.0.0',
    async init() {
        axios.defaults.paramsSerializer = function paramsSerializer(params) {
            return Qs.stringify(params, { indices: false });
        };
        const token : String = meta.auth.getToken();
        if (token !== undefined && token !== null && token !== '') axios.defaults.headers.Authorization = `Bearer ${token}`;
    },
    alert(message: string) {
        return new Promise<void>((resolve) => {
            store.commit('app/SET_ALERT', {
                value: true,
                message,
                callback() {
                    resolve();
                },
            });
        });
    },
    confirm(message: string, oktext = '예', canceltext = '아니오') {
        return new Promise((resolve) => {
            store.commit('app/SET_CONFIRM', {
                value: true,
                message,
                oktext,
                canceltext,
                callback(result: object) {
                    resolve(result);
                },
            });
        });
    },
    auth: {
        async login(username: string, password: string) {
            let authorization;
            let token;
            token = Basil.localStorage.get('token');
            if (token && await meta.auth.authenticated(token)) {
                authorization = `Bearer ${token}`;
            } else {
                authorization = (await axios({
                    url: '/api/login',
                    method: 'post',
                    data: {
                        username,
                        password,
                    },
                })).headers.authorization;
                token = authorization.replace('Bearer ', '');
                Basil.localStorage.set('token', token);
            }
            axios.defaults.headers.Authorization = authorization;
        },
        async logout(token: string) {
            await axios({
                url: '/api/logout',
                method: 'post',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            Basil.localStorage.remove('token');
            delete axios.defaults.headers.Authorization;
        },
        async signUp(data: object) {
            return axios({
                url: '/api/app/accounts/sign-up',
                method: 'post',
                data,
            });
        },
        async idExists(data: object) {
            return axios({
                url: '/api/common/users/id-exists',
                method: 'post',
                data,
            });
        },
        async emailExists(data: object) {
            return axios({
                url: '/api/common/users/email-exists',
                method: 'post',
                data,
            });
        },
        async phoneExists(data: object) {
            return axios({
                url: '/api/common/users/phone-exists',
                method: 'post',
                data,
            });
        },
        async snsExists(data: object) {
            return axios({
                url: '/api/common/users/sns-exists',
                method: 'post',
                data,
            });
        },
        async authenticated(token: string) {
            if (token) {
                try {
                    await axios({
                        url: '/api',
                        method: 'get',
                        headers: {
                            Authorization: token,
                        },
                    });
                    return true;
                } catch (e) {
                    return false;
                }
            } else {
                return false;
            }
        },
        async authorize(token: string) {
            let authentication = {
                user: Object,
                roleList: Object,
                menuList: Object,
                treeMenuList: Object,
            };
            const storeTemp: any = store;

            authentication = jwtDecode(token);
            store.commit('app/SET_TOKEN', token);
            store.commit('app/SET_USER', authentication.user);
            store.commit('app/SET_ROLE_LIST', authentication.roleList);
            const session = (await axios({ url: `/api/common/menus/session/${storeTemp.state.app.user.id}`, method: 'get' })).data;
            store.commit('app/SET_MENU_LIST', session.menuList);
            store.commit('app/SET_TREE_MENU_LIST', session.treeMenuList);
            store.commit('app/SET_TOP_MENU_CODE', ((await meta.api.common.code.getCode(32)).data.value));
            store.commit('app/SET_CODE_LIST', ((await meta.api.common.code.getCodeList({ page: 1, rowSize: 10000000 })).data.items));
            axios.defaults.headers.Authorization = `Bearer ${token}`;
        },
        unauthorize() {
            store.commit('app/SET_TOKEN', null);
            store.commit('app/SET_USER', null);
            store.commit('app/SET_ROLE_LIST', null);
            store.commit('app/SET_MENU_LIST', null);
            store.commit('app/SET_TREE_MENU_LIST', null);
            store.commit('app/SET_TOP_MENU_CODE', null);
            store.commit('app/SET_CODE_LIST', null);
            delete axios.defaults.headers.Authorization;
            Basil.localStorage.remove('token');
        },
        getToken() {
            return Basil.localStorage.get('token');
        },
    },
    config: {
        getCodeNameById(id: any) {
            const row = (store.getters['app/GET_CODE_LIST']).find((e: any) => e.id === id);
            if (row !== undefined && row != null && row.name !== undefined && row.name != null) {
                return row.name;
            }

            return null;
        },
        getCodeValueById(id: any) {
            const row = (store.getters['app/GET_CODE_LIST']).find((e: any) => e.id === id);
            if (row !== undefined && row != null && row.value !== undefined && row.value != null) {
                return row.value;
            }

            return null;
        },
        getCodeDescriptionById(id: any) {
            const row = (store.getters['app/GET_CODE_LIST']).find((e: any) => e.id === id);
            if (row !== undefined && row != null && row.description !== undefined && row.description != null) {
                return row.description;
            }

            return null;
        },
        getCodeRowById(id: any) {
            return (store.getters['app/GET_CODE_LIST']).find((e: any) => e.id === id);
        },
        getCodeListByParentId(parentId: Number) {
            return (store.getters['app/GET_CODE_LIST']).filter((e: any) => e.parentId === parentId);
        },
        getCodeListByParentName(parentName: String) {
            const parentRow = (store.getters['app/GET_CODE_LIST']).find((e: any) => e.parentId == null && e.name === parentName);

            if (parentRow !== undefined && parentRow != null && parentRow.id !== undefined && parentRow != null) {
                return (store.getters['app/GET_CODE_LIST']).filter((e: any) => e.parentId === parentRow.id);
            }
            return null;
        },
    },
    api: {
        common: {
            code: {
                getCodeList(params : object) { return axios({ url: '/api/common/codes', method: 'get', params }); },
                getCode(id : number) { return axios({ url: `/api/common/codes/${id}`, method: 'get' }); },
                createCode(data: object) { return axios({ url: '/api/common/codes', method: 'post', data }); },
                modifyCode(id: number, data: object) { return axios({ url: `/api/common/codes/${id}`, method: 'put', data }); },
                removeCode(id: number) { return axios({ url: `/api/common/codes/${id}`, method: 'delete' }); },
            },
            api: {
                getApiList(params: object) { return axios({ url: '/api/common/apis', method: 'get', params }); },
                getApi(id: number) { return axios({ url: `/api/common/apis/${id}`, method: 'get' }); },
                createApi(data: object) { return axios({ url: '/api/common/apis', method: 'post', data }); },
                modifyApi(id: number, data: object) { return axios({ url: `/api/common/apis/${id}`, method: 'put', data }); },
                removeApiList(data: object) { return axios({ url: '/api/common/apis', method: 'delete', data }); },
            },
            menu: {
                getMenuList(params: object) { return axios({ url: '/api/common/menus', method: 'get', params }); },
                getMenu(id: number) { return axios({ url: `/api/common/menus/${id}`, method: 'get' }); },
                createMenu(data: object) { return axios({ url: '/api/common/menus', method: 'post', data }); },
                modifyMenu(id: number, data: object) { return axios({ url: `/api/common/menus/${id}`, method: 'put', data }); },
                removeMenu(id: number) { return axios({ url: `/api/common/menus/${id}`, method: 'delete' }); },
            },
            role: {
                getRoleList(params: object) { return axios({ url: '/api/common/roles', method: 'get', params }); },
                getRole(id: number) { return axios({ url: `/api/common/roles/${id}`, method: 'get' }); },
                createRole(data: any) { return axios({ url: '/api/common/roles', method: 'post', data }); },
                modifyRole(id: number, data: any) { return axios({ url: `/api/common/roles/${id}`, method: 'put', data }); },
                removeRoleAllDependencyList(data: any) { return axios({ url: '/api/common/roles/delete-all-dependency', method: 'delete', data }); },
                removeRole(id: number) { return axios({ url: `/api/common/roles/${id}`, method: 'delete' }); },
            },
            roleApi: {
                getRoleApiList(params: object) { return axios({ url: '/api/common/role-apis', method: 'get', params }); },
                getRoleApi(roleId: number, apiId: number) { return axios({ url: `/api/common/role-apis/${roleId},${apiId}`, method: 'get' }); },
            },
            roleMenu: {
                getRoleMenuList(params: object) { return axios({ url: '/api/common/role-menus', method: 'get', params }); },
                getRoleMenu(roleId: number, menuId: number) { return axios({ url: `/api/common/role-menus/${roleId},${menuId}`, method: 'get' }); },
            },
            roleUser: {
                getRoleUserList(params: object) { return axios({ url: '/api/common/role-users', method: 'get', params }); },
                modifyRoleUser(params: object) { return axios({ url: '/api/common/role-users/teacher', method: 'put', params }); },
            },
            user: {
                createUser(data: any) { return axios({ url: '/api/common/users', method: 'post', data }); },
                getUserList(params: object) { return axios({ url: '/api/common/users', method: 'get', params }); },
                getUser(id: number) { return axios({ url: `/api/common/users/${id}`, method: 'get' }); },
                getUserByName(params: any) { return axios({ url: '/api/common/users/findId', method: 'get', params }); },
                getUserByUserName(params: any) { return axios({ url: '/api/common/users/findPw', method: 'get', params }); },
                modifyUser(id: number, data: object) { return axios({ url: `/api/common/users/${id}`, method: 'put', data }); },
                modifyPassword(id: number, data: object) { return axios({ url: `/api/common/users/password/${id}`, method: 'put', data }); },
                modifyPassword2(id: number, data: object) { return axios({ url: `/api/common/users/findPw/${id}`, method: 'put', data }); },
                modifyUserTeacherAuth(id: number, data: any) { return axios({ url: `/api/common/users/teacher-auth/${id}`, method: 'put', data }); },
                getTeacherList(params: object) { return axios({ url: '/api/common/users/teacher-list', method: 'get', params }); },
                getStudentList(params: object) { return axios({ url: '/api/common/users/student-list', method: 'get', params }); },
            },
            terms: {
                getTermsList(params: object) { return axios({ url: '/api/common/terms', method: 'get', params }); },
            },
            consulting: {
                getConsultingList(params : object) { return axios({ url: '/api/common/consultings', method: 'get', params }); },
                getConsulting(id: number) { return axios({ url: `/api/common/consultings/${id}`, method: 'get' }); },
                getConsultingByPhoneNum(phoneNum : string) { return axios({ url: '/api/common/consultings/phone-num', method: 'get', params: { phoneNum } }); },
                createConsulting(data: object) { return axios({ url: '/api/common/consultings', method: 'post', data }); },
                modifyConsultingContactStatus(id: number, data: any) { return axios({ url: `/api/common/consultings/${id}`, method: 'put', data }); },
            },
            student: {
                classType: {
                    getStudentClassTypeList(params: object) { return axios({ url: '/api/common/student/class-type', method: 'get', params }); },
                    getStudentClassType(id: number) { return axios({ url: `/api/common/student/class-type/${id}`, method: 'get' }); },
                    createStudentClassType(data: any) { return axios({ url: '/api/common/student/class-type', method: 'post', data }); },
                    modifyStudentClassType(id: number, data: any) { return axios({ url: `/api/common/student/class-type/${id}`, method: 'put', data }); },
                    removeStudentClassTypeList(data: any) { return axios({ url: '/api/common/student/class-type', method: 'delete', data }); },
                    removeStudentClassType(id: number) { return axios({ url: `/api/common/student/class-type/${id}`, method: 'delete' }); },
                },
                subject: {
                    getStudentSubjectList(params: object) { return axios({ url: '/api/common/student/subject', method: 'get', params }); },
                    getStudentSubject(id: number) { return axios({ url: `/api/common/student/subject/${id}`, method: 'get' }); },
                    createStudentSubjectList(data: any) { return axios({ url: '/api/common/student/subject?bulk', method: 'post', data }); },
                    createStudentSubject(data: any) { return axios({ url: '/api/common/student/subject', method: 'post', data }); },
                    modifyStudentSubject(id: number, data: any) { return axios({ url: `/api/common/student/subject/${id}`, method: 'put', data }); },
                    removeStudentSubjectList(data: any) { return axios({ url: '/api/common/student/subject', method: 'delete', data }); },
                    removeStudentSubject(id: number) { return axios({ url: `/api/common/student/subject/${id}`, method: 'delete' }); },
                },
                time: {
                    getStudentTimeList(params: object) { return axios({ url: '/api/common/student/time', method: 'get', params }); },
                    getStudentTime(id: number) { return axios({ url: `/api/common/student/time/${id}`, method: 'get' }); },
                    createStudentTimeList(data: any) { return axios({ url: '/api/common/student/time?bulk', method: 'post', data }); },
                    createStudentTime(data: any) { return axios({ url: '/api/common/student/time', method: 'post', data }); },
                    modifyStudentTime(id: number, data: any) { return axios({ url: `/api/common/student/time/${id}`, method: 'put', data }); },
                    removeStudentTimeList(data: any) { return axios({ url: '/api/common/student/time', method: 'delete', data }); },
                    removeStudentTime(id: number) { return axios({ url: `/api/common/student/time/${id}`, method: 'delete' }); },
                },
                curriculum: {
                    getCurriculumDetailList(id: number) { return axios({ url: `/api/common/student/curriculum/${id}`, method: 'get' }); },
                    createCurriculum(subjectId: number, data: any) { return axios({ url: `/api/common/student/curriculum/${subjectId}`, method: 'post', data }); },
                },
                calander: {
                    getCalanderList(userId: number, params: object) { return axios({ url: `/api/common/day/${userId}`, method: 'get', params }); },
                    createCalander(userId: number, data: any) { return axios({ url: `/api/common/day/${userId}`, method: 'post', data }); },
                    modifyCalander(id:number, data: object) { return axios({ url: `/api/common/day/${id}`, method: 'put', data }); },
                    deleteCalander(id:number) { return axios({ url: `/api/common/day/${id}`, method: 'delete' }); },
                    getDday(userId: number, params: object) { return axios({ url: `/api/common/students/main/${userId}/dDay`, method: 'get', params }); },
                    getCurriculum(userId: number) { return axios({ url: `/api/common/students/main/${userId}/curriculum`, method: 'get' }); },
                    getCurriculumWeekList(id: number) { return axios({ url: `/api/common/students/main/${id}/curriculum-week`, method: 'get' }); },
                },
                homework: {
                    getHomeworkList(params: object) { return axios({ url: '/api/common/homework', method: 'get', params }); },
                    getHomework(id:number) { return axios({ url: `/api/common/homework/${id}/detail`, method: 'get' }); },
                    getHomeworkDetail(id:number) { return axios({ url: `/api/common/homework/${id}/detail-view`, method: 'get' }); },
                    addHomeworkFile(id: number, data: any) { return axios({ url: `/api/common/homework/${id}/upload`, method: 'post', data }); },
                    getTeacherList(userId: number) { return axios({ url: `/api/common/homework/${userId}/teacherList`, method: 'get' }); },
                    updateQuetion(id:number, data: any) { return axios({ url: `/api/common/homework/${id}/updateQuetion`, method: 'put', data }); },
                    deleteHomework(data: any) { return axios({ url: '/api/common/homework/deleteHomework', method: 'delete', data }); },
                    async downloadFile(params: { filename: string; originFilename: string; }) {
                        const { data } = (await axios({
                            url: `/api/common/homework/downloadFile/${params.filename}`,
                            responseType: 'blob',
                            method: 'get',
                        }));
                        const url = window.URL.createObjectURL(data);
                        const a = document.createElement('a');
                        a.setAttribute('href', url);
                        a.setAttribute('download', params.originFilename);
                        a.click();
                        window.URL.revokeObjectURL(url);
                    },
                },
                record: {
                    getSubjectList(userId: number) { return axios({ url: `/api/common/record/${userId}/subjectList` }); },
                    getRecordList(params: object) { return axios({ url: '/api/common/record/recordList', method: 'get', params }); },
                    addRecord(data: any) { return axios({ url: '/api/common/record/addRecord', method: 'post', data }); },
                },
                reservation: {
                    getCurriculumList(studentId: number) { return axios({ url: `/api/common/student/reservation/${studentId}`, method: 'get' }); },
                    getCurriculum(params: object) { return axios({ url: '/api/common/student/reservation/getCurriculum', method: 'get', params }); },
                    getReservationList(studentId: number) { return axios({ url: `/api/common/student/reservation/${studentId}/getReservationList`, method: 'get' }); },
                    addReservation(data: any) { return axios({ url: '/api/common/student/reservation/addReservation', method: 'post', data }); },
                },
                evaluation: {
                    getStudentEvaluationCheckList(params: object) { return axios({ url: '/api/common/student/evaluation/check/list', method: 'get', params }); },
                    getStudentEvaluationList(params: object) { return axios({ url: '/api/common/student/evaluation/list', method: 'get', params }); },
                    getStudentEvaluation(params: object) { return axios({ url: '/api/common/student/evaluation/', method: 'get', params }); },
                    createStudentEvaluation(data: object) { return axios({ url: '/api/common/student/evaluation', method: 'post', data }); },
                },
            },
            teacher: {
                subject: {
                    getTeacherParentSubjectList(params: object) { return axios({ url: '/api/common/teacher/parent-subject', method: 'get', params }); },
                    getTeacherSubjectList(params: object) { return axios({ url: '/api/common/teacher/subject', method: 'get', params }); },
                    createTeacherSubjectList(data: any) { return axios({ url: '/api/common/teacher/subject?bulk', method: 'post', data }); },

                },
                classType: {
                    getTeacherClassTypeList(params: object) { return axios({ url: '/api/common/teacher/class-type', method: 'get', params }); },
                    createTeacherClassTypeList(data: any) { return axios({ url: '/api/common/teacher/class-type?bulk', method: 'post', data }); },
                    createTeacherClassType(data: any) { return axios({ url: '/api/common/teacher/class-type', method: 'post', data }); },
                },
                time: {
                    getTeacherTimeList(params: object) { return axios({ url: '/api/common/teacher/time', method: 'get', params }); },
                    createTeacherTimeList(data: any) { return axios({ url: '/api/common/teacher/time?bulk', method: 'post', data }); },
                },
                auth: {
                    getTeacherAuth(id: number) { return axios({ url: `/api/common/teacher/auth/${id}`, method: 'get' }); },
                    getTeacherAuthList(params: object) { return axios({ url: '/api/common/teacher/auth', method: 'get', params }); },
                    createTeacherAuth(data: any) { return axios({ url: '/api/common/teacher/auth', method: 'post', data }); },
                    modifyTeacherAuth(id:number, data: any) { return axios({ url: `/api/common/teacher/auth/${id}`, method: 'put', data }); },
                    getTeacherAuthSubjectList(params: object) { return axios({ url: '/api/common/teacher/auth-subject', method: 'get', params }); },
                },
                calendar: {
                    getCurriculumList(teacherId: number) { return axios({ url: `/api/common/teachers/main/${teacherId}/curriculumList`, method: 'get' }); },
                    getStudentList(teacherId: number) { return axios({ url: `/api/common/teachers/main/${teacherId}/studentList`, method: 'get' }); },
                    getStudentBySubject(id: number) { return axios({ url: `/api/common/teachers/main/${id}/student-subject`, method: 'get' }); },
                },
                homework: {
                    getHomeworkList(params: object) { return axios({ url: '/api/common/teachers/main/homeworkList', method: 'get', params }); },
                    getHomework(id: number) { return axios({ url: `/api/common/teachers/main/${id}/homework`, method: 'get' }); },
                    updateFinish(id: number) { return axios({ url: `/api/common/teachers/main/${id}/finishYn`, method: 'put' }); },
                    createHomework(data: any) { return axios({ url: '/api/common/teachers/main/createHomework', method: 'post', data }); },
                    modifyHomework(data: any) { return axios({ url: '/api/common/teachers/main/modifyHomework', method: 'put', data }); },
                    deleteHomework(data: any) { return axios({ url: '/api/common/teachers/main/deleteHomework', method: 'delete', data }); },
                    downloadFile(params: any) {
                        return axios({
                            url: '/api/common/teachers/main/downloadFile', method: 'get', params, responseType: 'blob',
                        });
                    },
                },
                reservation: {
                    getReservationList(teacherId: number) { return axios({ url: `/api/common/teachers/reservation/${teacherId}`, method: 'get' }); },
                    getFinishReservationList(teacherId: number) { return axios({ url: `/api/common/teachers/reservation/${teacherId}/finishReservationList`, method: 'get' }); },
                    getReservation(id: number) { return axios({ url: `/api/common/teachers/reservation/${id}/getReservation`, method: 'get' }); },
                    updateReservation(data: object) { return axios({ url: '/api/common/teachers/reservation/updateReservation', method: 'put', data }); },
                },
                evaluation: {
                    getTeacherEvaluationList(params: object) { return axios({ url: '/api/common/teachers/evaluation/list', method: 'get', params }); },
                    getTeacherEvaluation(id: number) { return axios({ url: `/api/common/teachers/evaluation/${id}`, method: 'get' }); },
                    createTeacherEvaluation(data: object) { return axios({ url: '/api/common/teachers/evaluation', method: 'post', data }); },
                },
            },
            tutoringApplication: {
                getTutoringApplicationList(params: object) { return axios({ url: '/api/common/tutoring-applications', method: 'get', params }); },
                getTutoringApplication(id: number) { return axios({ url: `/api/common/tutoring-applications/${id}`, method: 'get' }); },
                getTutoringApplicationStudentList(params: object) { return axios({ url: '/api/common/tutoring-applications/student/tutoring-list', method: 'get', params }); },
                getTutoringApplicationTeacherList(params: object) { return axios({ url: '/api/common/tutoring-applications/teacher/tutoring-list', method: 'get', params }); },
                getTutoringApplicationBySubjectId(subjectId: object) { return axios({ url: `/api/common/tutoring-applications/subject-id/${subjectId}`, method: 'get' }); },
                getTutoringApplicationByOrderId(orderId: object) { return axios({ url: `/api/common/tutoring-applications/order-id/${orderId}`, method: 'get' }); },
                getStudentTutoringApplication(params: object) { return axios({ url: '/api/common/tutoring-applications/student', method: 'get', params }); },
                createTutoringApplication(data: any) { return axios({ url: '/api/common/tutoring-applications', method: 'post', data }); },
                modifyTutoringApplication(data: any) { return axios({ url: '/api/common/tutoring-applications', method: 'put', data }); },
                getTutoringApplcationDetailByTutoringId(id: number) { return axios({ url: `/api/common/tutoring-applications/detail/tutoring/${id}`, method: 'get' }); },
                createTutoringApplicationDetail(data: any) { return axios({ url: '/api/common/tutoring-applications/detail', method: 'post', data }); },
                modifyTutoringApplicationSubject(data: any) { return axios({ url: '/api/common/tutoring-applications/subject', method: 'put', data }); },
                getTutoringCalendar(params: object) { return axios({ url: '/api/common/tutoring-applications/student/calendar', method: 'get', params }); },
            },
            notice: {
                getNoticeList(params: object) { return axios({ url: '/api/common/notice', method: 'get', params }); },
                getNotice(params: object) { return axios({ url: '/api/common/notice/detail', method: 'get', params }); },
                createNotice(data: any) { return axios({ url: '/api/common/notice/crated', method: 'post', data }); },
            },
            community: {
                getCommunityList(params: object) { return axios({ url: '/api/common/community', method: 'get', params }); },
                getCommunityImageList(params: object) { return axios({ url: '/api/common/community/image', method: 'get', params }); },
                getCommunity(params: object) { return axios({ url: '/api/common/community/detail', method: 'get', params }); },
                createCommunity(data: any) { return axios({ url: '/api/common/community/', method: 'post', data }); },
                createCommunityImage(data: any) { return axios({ url: '/api/common/community/crated', method: 'post', data }); },
                deleteCommunity(data: any) { return axios({ url: '/api/common/community/delete', method: 'put', data }); },
                deleteCommunityImage(params: any) { return axios({ url: '/api/common/community/delete/file', method: 'put', params }); },
            },
            faq: {
                getFaq(id: number) { return axios({ url: `/api/common/faq/${id}`, method: 'get' }); },
                getFaqList(params: object) { return axios({ url: '/api/common/faq', method: 'get', params }); },
                createdFaq(data: any) { return axios({ url: '/api/common/faq', method: 'post', data }); },
                modifyFaq(id: number, data: any) { return axios({ url: `/api/common/faq/${id}`, method: 'put', data }); },
                removeFaq(id: number) { return axios({ url: `/api/common/faq/${id}`, method: 'delete' }); },
            },
            faqCategory: {
                getFaqCategoryList(params: object) { return axios({ url: '/api/common/faq-categories', method: 'get', params }); },
                getFaqCategory(id: number) { return axios({ url: `/api/common/faq-categories/${id}`, method: 'get' }); },
            },
            chat: {
                findChatList(userId: number) { return axios({ url: `/api/common/chat/${userId}`, method: 'get' }); },
                findStudentList(userId: number) { return axios({ url: `/api/common/chat/student/${userId}`, method: 'get' }); },
                findChatRoom(userId: number, teacherId: number) { return axios({ url: `/api/common/chat/${userId}/${teacherId}`, method: 'get' }); },
            },
            studentScore: {
                createStucentScore(data: any) { return axios({ url: '/api/common/student-score/save', method: 'post', data }); },
                getStudentScoreList(params: object) { return axios({ url: '/api/common/student-score/list', method: 'get', params }); },
                deleteStudentScore(id: number) { return axios({ url: `/api/common/student-score/delete/${id}`, method: 'delete' }); },
            },
            popUp: {
                getCookie() { return axios({ url: 'api/common/cookie', method: 'get' }); },
                createCookie() { return axios({ url: 'api/common/cookie', method: 'post' }); },
            },
        },
        app: {
            account: {
                createAccount(data: object) { return axios({ url: '/api/app/accounts', method: 'post', data }); },
                modifyAccount(data: object) { return axios({ url: '/api/app/accounts', method: 'put', data }); },
                removeAccount(id: number) { return axios({ url: `/api/app/accounts/${id}`, method: 'delete' }); },
                signUp(data: object) {
                    return axios({
                        url: '/api/app/accounts/sign-up',
                        method: 'post',
                        data,
                    });
                },
            //     removeAccount(id: number) { return axios({ url: `/api/app/accounts/${id}`, method: 'delete' }); },
            //     signUp(data: object) { return axios({ url: '/api/app/accounts/sign-up', method: 'post', data }); },
            },
            RoleAndRoleMenuAndRoleApi: {
                createRoleAndRoleMenuAndRoleApi(data: object) { return axios({ url: '/api/app/role-menu-apis', method: 'post', data }); },
                modifyRoleAndRoleMenuAndRoleApi(data: object) { return axios({ url: '/api/app/role-menu-apis', method: 'put', data }); },
            },
            dataUpload: {
                async getFile(params: any) {
                    const { data } = (await axios({
                        url: `/api/app/data-uploads/image/${params.subPath}/${params.filename}`,
                        responseType: 'blob',
                        method: 'get',
                    }));
                    const url = window.URL.createObjectURL(data);
                    const a = document.createElement('a');
                    a.setAttribute('href', url);
                    a.setAttribute('download', params.originFilename);
                    a.click();
                    window.URL.revokeObjectURL(url);
                },
                async getZipFile(params: any) {
                    const { data } = (await axios({
                        url: `/api/app/data-uploads/zip/${params.subPath}/${params.classId}`,
                        responseType: 'blob',
                        method: 'get',
                        params,
                    }));
                    const url = window.URL.createObjectURL(data);
                    const a = document.createElement('a');
                    a.setAttribute('download', params.zipname);
                    a.setAttribute('href', url);
                    a.click();
                    window.URL.revokeObjectURL(url);
                },
            },
            mailSend: {
                sendTempPassword(data: object) { return axios({ url: '/api/app/email/temp-password', method: 'post', data }); },
            },
        },
        util: {
            menu: {
                getTreeMenuList(params: object) { return axios({ url: '/api/util/menus/tree-menus', method: 'get', params }); },
                getDefaultMenuList() { return axios({ url: '/api/menu', method: 'get' }); },
            },
            code: {
                getTreeCodeList(params: object) { return axios({ url: '/api/util/codes/tree-codes', method: 'get', params }); },
            },
        },
    },
    util: {
        sort(sortBy: string, sortDesc: string) {
            const str = [];
            if (sortBy.length > 0 && sortDesc.length > 0) {
                for (let i = 0; i < sortBy.length; i += 1) {
                    str.push(`${sortBy[i]},${sortDesc[i]? 'desc' : 'asc'}`);
                }
            }
            return str;
        },
    },
};

meta.init();

export default meta;
